import React from 'react'
import { Currency } from '@mobi/utils/money'
import { dayjs } from '@mobi/utils/date'
import { BetCard } from '@mobi/betslip/Components/Common/BetCard'
import { StatusBadge } from '@mobi/component-library/Common/StatusBadge'
import { BetCardTitle } from '@mobi/betslip/Components/Common/BetCardTitle'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { colors } from '@mobi/component-library/Theme/Common'
import type { BetLoopShareTicket } from '@core/Areas/BetLoop/types'
import type { Ticket, TicketStatus } from '@core/Areas/AccountActivity/types'
import { TicketDisplayContext } from '@core/Areas/AccountActivity/context'
import { StatusSilks } from '@core/Areas/AccountActivity/Components/Ticket/Components/StatusSilks'
import { getStatusText } from '@core/Areas/AccountActivity/Components/Ticket/helpers/getTicketStatus'
import { CountdownStatus } from '../Common/CountdownStatus'

export const Multi: React.FC<{ ticket: Ticket | BetLoopShareTicket; status: TicketStatus }> = ({
  ticket,
  status,
}) => {
  const ticketContext = React.useContext(TicketDisplayContext)
  const isPreviewMode = ticketContext.type === 'share'
  const isBetLoopQuickShare = isPreviewMode && ticketContext.data.display === 'betloop-quick-share'

  const formulaText = ticket.formula?.formulaString ? ` · ${ticket.formula.formulaString}` : ''
  const multiTitleText = `${ticket.legs.length} Leg Multi${formulaText}`
  const multiCombinedReturn = ticket.combinedDividend
    ? new Currency(ticket.combinedDividend).format()
    : ''

  const isPending = status === 'Pending'
  const finishedLegAst = !isPending ? getMultiFinishedLegAst(ticket, status) : null
  const headingText = isPending
    ? 'Next leg begins in:'
    : finishedLegAst
      ? `Finished on ${dayjs(finishedLegAst).format('ddd Do MMM')}`
      : undefined

  if (isPreviewMode && !isBetLoopQuickShare) {
    return (
      <BetCardTitle
        title={multiTitleText}
        rightComponent={multiCombinedReturn}
        bottomLeftComponent={<StatusSilks ticket={ticket as Ticket} />}
        bottomRightComponent={
          ticket.status !== 'Pending' && (
            <StatusBadge badgeSize='S' color={status === 'Won' ? 'success' : 'neutral'}>
              {getStatusText(status)}
            </StatusBadge>
          )
        }
      />
    )
  }

  return (
    <>
      <BetCard.EventDetails
        heading={headingText}
        statusComponent={<CountdownStatus ticket={ticket} status={status} />}
        iconComponent={null}
      />

      <BetCard.SelectionInfo
        heading={multiTitleText}
        subHeading={
          <>
            {ticket.legs.map(leg => {
              const { name, number } = leg.selections[0].selectionDetails[0].acceptorDetails[0]
              return (
                <React.Fragment key={number + name + leg.advertisedStartTime}>
                  {`${number ? number + ' - ' : ''}${name}`}
                  <br />
                </React.Fragment>
              )
            })}
          </>
        }
        priceWin={multiCombinedReturn}
        iconComponent={<Icon size='2.4rem' name='CustomBetslip' color={colors.neutral[800]} />}
      />
    </>
  )
}

// =============
// Local Helpers
// =============

function getMultiFinishedLegAst(
  ticket: Ticket | BetLoopShareTicket,
  status: TicketStatus
): string | null {
  if (status === 'Won') {
    const latestAst = ticket.legs.reduce<string>(reduceCallbackToGetLatestAst, '')
    return latestAst
  }

  if (status === 'NoPayout') {
    const latestAst = ticket.legs
      .filter(leg => leg.result === 'LOSE')
      .reduce<string>(reduceCallbackToGetLatestAst, '')
    return latestAst
  }

  return null
}

function reduceCallbackToGetLatestAst(latestAst: string, curr: Ticket['legs'][0]) {
  if (!latestAst) return curr.advertisedStartTime
  if (dayjs(latestAst).isBefore(curr.advertisedStartTime)) return curr.advertisedStartTime
  return latestAst
}
